import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

import { INITIAL_DOMINANCE, resolveDominant, shareKey, SPEAKER_HYSTERESIS_MS } from './dominant';
import type { DominanceInput, DominanceState } from './dominant';
import { gridWindow } from './gridLimits';
import { computeGridLayout, computeRowLayout, TILE_GAP_PX } from './gridLayout';
import { speakingIdentities } from './roomState';
import type { ParticipantTile, ScreenShareTile } from './roomState';
import { useArrivals } from './ShareStage';
import { ScreenTile } from './ScreenTile';
import { SelfPip } from './SelfPip';
import { tileSubscriptionDimensions } from './subscribeQuality';
import type { TileQualityRole } from './subscribeQuality';
import { VideoTile } from './VideoTile';

export interface MeetingLayoutProps {
  /** Every participant's camera tile, derivation order (local first). */
  readonly tiles: readonly ParticipantTile[];
  /** Every live screen share; each renders as its own tile, like a participant. */
  readonly shares: readonly ScreenShareTile[];
  /** Active speaker identities, loudest first (RoomEvent.ActiveSpeakersChanged order). */
  readonly activeSpeakers: readonly string[];
  /** The user's pinned tile key, or null (automatic mode). */
  readonly pinnedKey: string | null;
  /** Mobile: dominant on top, the rest in one dynamic horizontal strip below. */
  readonly isMobile: boolean;
  /** Horizontally flips the own camera tile (the "mirror video" option). */
  readonly mirrorOwnVideo: boolean;
  readonly isAdminIdentity: (identity: string) => boolean;
  /** Live hand state per identity (own optimistic flag merged with presence). */
  readonly handRaisedFor: (identity: string) => boolean;
  /** Pin/unpin toggle for a tile key; clicking a tile and its pin button both route here. */
  readonly onTogglePin: (key: string) => void;
}

/** Share of the main area the dominant slot takes while other tiles are rendered. */
const DOMINANT_SHARE = 0.7;

/**
 * Vertical space the pagination pager keeps under the rest grid when it renders (prev/status/
 * next row plus its padding and breathing room). The side layout subtracts it from the side
 * box while paginated, so a sparse last page's big tiles are sized to stop ABOVE the pager
 * instead of overflowing under it - a tile's bottom nameplate band (z-20) used to cover the
 * pager's flip-back button and swallow its clicks.
 */
const GRID_PAGER_RESERVE_PX = 56;

/** One renderable slot of the unified grid: a participant camera or a screen share. */
interface TileSlot {
  readonly kind: 'camera' | 'share';
  readonly key: string;
  readonly identity: string;
  readonly camera: ParticipantTile | null;
  readonly share: ScreenShareTile | null;
}

/**
 * The unified meeting layout (the fullscreen dynamic grid): ONE dominant slot takes ~70% of
 * the area between the top bar and the control bar (left on wide containers, top otherwise;
 * on mobile it sits above a single dynamic strip), and every other participant - and every
 * other screen share, shares are just tiles - packs the remaining space in 16:9 tiles sized
 * by {@link computeGridLayout}, growing and shrinking as people join and leave. With one
 * participant the single tile fills the area (bounded by the 16:9 fit, letterboxed), which is
 * the fullscreen fix.
 *
 * The 1:1 special case: with EXACTLY two participants the grid renders only the remote side -
 * the own camera tile leaves the grid and floats as the draggable, resizable self PiP
 * ({@link SelfPip}) over the dominant stream. The dominance chain itself is untouched: it
 * simply runs on the reduced input (pin > newest share > active speaker with hysteresis >
 * stable fallback, remote camera and shares only). When a share takes the slot in a 1:1, the
 * remote camera packs into the rest grid as a normal small tile beside the PiP. A third
 * participant folds the PiP back into the ordinary adaptive grid.
 *
 * Dominance priority: pinned tile (click again releases) > newest share > active speaker
 * after a 1.5 s continuous-speech hysteresis > the stable fallback (previous dominant, else
 * the first camera tile). Clicking a tile pins it; clicking the pinned dominant tile again
 * unpins back to automatic.
 */
export function MeetingLayout(props: MeetingLayoutProps): JSX.Element {
  const { t } = useTranslation();
  // In a 1:1 the own camera renders in the PiP overlay, so the grid (and the dominance input)
  // sees only the remote side; with any other participant count the grid owns every tile.
  const localTile = props.tiles.find((tile) => tile.isLocal) ?? null;
  const oneToOne = props.tiles.length === 2 && localTile !== null;
  const gridTiles = oneToOne ? props.tiles.filter((tile) => !tile.isLocal) : props.tiles;
  const pipTile =
    oneToOne && localTile !== null && localTile.videoTrack !== null ? localTile : null;
  const slots: readonly TileSlot[] = [
    ...gridTiles.map((tile): TileSlot => ({
      kind: 'camera',
      key: tile.identity,
      identity: tile.identity,
      camera: tile,
      share: null,
    })),
    ...props.shares.map((share): TileSlot => ({
      kind: 'share',
      key: shareKey(share.identity),
      identity: share.identity,
      camera: null,
      share,
    })),
  ];
  const arrivals = useArrivals(props.shares);
  const speaking = speakingIdentities(props.tiles, props.activeSpeakers);
  const dominant = useDominantTile({
    cameraIdentities: gridTiles.map((tile) => tile.identity),
    shareIdentitiesNewestFirst: arrivals,
    pinnedKey: props.pinnedKey,
    speakerIdentities: props.activeSpeakers,
    nowMs: Date.now(),
  });

  const [box, setBox] = useState({ width: 0, height: 0 });
  const sectionRef = useRef<HTMLElement | null>(null);
  // The rest-grid pagination state (gridLimits.ts): only the windowed page's tiles render;
  // the requested page is clamped through gridWindow, so a departure that shrinks the grid
  // never strands the state on an out-of-range page.
  const [requestedPage, setRequestedPage] = useState(0);

  // Layout math, hoisted above the early returns so the subscription-policy effect below
  // can be declared unconditionally (hooks rules).
  const dominantSlot =
    dominant === null ? undefined : (slots.find((slot) => slot.key === dominant) ?? slots[0]);
  const allRest = dominantSlot === undefined ? [] : slots.filter((slot) => slot.key !== dominantSlot.key);
  // Large-room bound (M4 item 5): past GRID_MAX_TILES rest tiles only one page renders, and
  // the off-page camera publications are unsubscribed (the subscription effect below).
  const restPage = gridWindow(
    allRest.map((slot) => slot.key),
    requestedPage,
  );
  const paginated = restPage.pageCount > 1;
  const pageKeys = new Set<string>(restPage.keys);
  const rest = allRest.filter((slot) => pageKeys.has(slot.key));
  const rowMode = !props.isMobile && box.width >= box.height;

  // Dominant area: ~70% of the measured box while other tiles exist, the whole box otherwise.
  const dominantW = rest.length > 0 && rowMode ? Math.round(box.width * DOMINANT_SHARE) : box.width;
  const dominantH =
    rest.length > 0 && !rowMode ? Math.round(box.height * DOMINANT_SHARE) : box.height;
  const sideW = rest.length > 0 && rowMode ? box.width - dominantW - TILE_GAP_PX : box.width;
  const sideH = rest.length > 0 && !rowMode ? box.height - dominantH - TILE_GAP_PX : box.height;

  const dominantGrid = computeGridLayout(1, dominantW, dominantH);
  // While paginated the pager shares the side box (flex-col): size the tiles to the box MINUS
  // the pager reserve so no tile - a lone last-page tile above all - ever reaches the pager.
  const sideGridHeight = paginated ? sideH - GRID_PAGER_RESERVE_PX : sideH;
  const sideGrid =
    props.isMobile || !rowMode
      ? computeRowLayout(rest.length, sideW, sideGridHeight)
      : computeGridLayout(rest.length, sideW, sideGridHeight);

  // Last applied subscription request per slot key: the policy effect runs on every render
  // (the layout math lives in the render), and this guard keeps redundant SDK emissions off
  // the wire unless the role, the rendered size or the grid size actually moved.
  const appliedSubscriptions = useRef(new Map<string, string>());
  // Camera slots currently held unsubscribed by the pagination: the flip to a page re-subscribes
  // exactly these (the SDK no-ops an unchanged setSubscribed; this keeps even the call off).
  const unsubscribedRef = useRef(new Set<string>());

  useLayoutEffect(() => {
    if (dominantSlot === undefined || dominantGrid.tileWidth <= 0 || dominantGrid.tileHeight <= 0) {
      return;
    }
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    // The pagination's off-switch first: every camera slot OUTSIDE the rendered window is
    // unsubscribed - a subscribed track with no attached element would keep decoding, and
    // adaptiveStream's visibility pause only reaches elements that exist. Shares are exempt
    // (explicit user intent) and so is the dominant slot (always rendered).
    for (const slot of allRest) {
      if (slot.camera === null || pageKeys.has(slot.key)) {
        continue;
      }
      const control = slot.camera.videoSubscription;
      if (control === null || control.setSubscribed === undefined) {
        continue;
      }
      if (!unsubscribedRef.current.has(slot.key)) {
        unsubscribedRef.current.add(slot.key);
        control.setSubscribed(false);
      }
    }
    const applySlot = (
      slot: TileSlot,
      role: TileQualityRole,
      cssWidth: number,
      cssHeight: number,
    ): void => {
      if (slot.camera === null) {
        return;
      }
      const control = slot.camera.videoSubscription;
      if (control === null) {
        return;
      }
      // A slot returning from an off-page window re-subscribes before the ladder dims: the
      // SFU then forwards the layer the dims ask for, exactly as on first join.
      if (unsubscribedRef.current.delete(slot.key) && control.setSubscribed !== undefined) {
        control.setSubscribed(true);
      }
      const dims = tileSubscriptionDimensions(role, cssWidth, cssHeight, dpr, rest.length);
      const signature = `${dims.width}x${dims.height}`;
      if (appliedSubscriptions.current.get(slot.key) === signature) {
        return;
      }
      appliedSubscriptions.current.set(slot.key, signature);
      control.setVideoDimensions({ width: dims.width, height: dims.height });
    };
    applySlot(dominantSlot, 'stage', dominantGrid.tileWidth, dominantGrid.tileHeight);
    for (const slot of rest) {
      applySlot(slot, 'grid', sideGrid.tileWidth, sideGrid.tileHeight);
    }
  });

  useLayoutEffect(() => {
    const element = sectionRef.current;
    if (element === null) {
      return;
    }
    const measure = (): void => {
      setBox((current) =>
        current.width === element.clientWidth && current.height === element.clientHeight
          ? current
          : { width: element.clientWidth, height: element.clientHeight },
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      // jsdom and old engines: no observer, keep the mount-time measure plus window resizes.
      window.addEventListener('resize', measure);
      return () => {
        window.removeEventListener('resize', measure);
      };
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  if (dominant === null || slots.length === 0 || dominantSlot === undefined) {
    // The measured section root stays mounted even while the room is empty: the size effect
    // attaches to it once, on mount, and the ResizeObserver keeps the box fresh afterwards.
    return (
      <section
        ref={sectionRef}
        data-testid="meeting-layout"
        aria-label={t('room.participants_aria')}
        className="min-h-0 flex-1"
      />
    );
  }

  return (
    <section
      ref={sectionRef}
      data-testid="meeting-layout"
      aria-label={t('room.participants_aria')}
      className="relative min-h-0 flex-1 overflow-hidden"
    >
      <div
        className={cn('flex h-full w-full min-h-0', rowMode ? 'flex-row' : 'flex-col')}
        style={{ gap: TILE_GAP_PX }}
      >
        <div
          data-testid="grid-dominant"
          aria-label={t('room.dominant_aria')}
          className="flex min-h-0 min-w-0 items-center justify-center"
          style={rowMode ? { width: dominantW } : { height: dominantH }}
        >
          <div
            data-testid="dominant-surface"
            className="relative cursor-pointer overflow-hidden rounded-xl bg-zinc-900 ring-1 ring-white/[0.06]"
            style={{
              width: dominantGrid.tileWidth,
              height: dominantGrid.tileHeight,
            }}
            onClick={(event) => {
              // Same rule as the strip: a click that starts on an inner button (the pin
              // affordance) belongs to the button only, or it would toggle the pin twice.
              if (event.target instanceof Element && event.target.closest('button') !== null) {
                return;
              }
              props.onTogglePin(dominantSlot.key);
            }}
          >
            <SlotSurface
              slot={dominantSlot}
              speaking={speaking.has(dominantSlot.identity)}
              pinned={props.pinnedKey === dominantSlot.key}
              isAdmin={props.isAdminIdentity(dominantSlot.identity)}
              handRaised={props.handRaisedFor(dominantSlot.identity)}
              mirror={props.mirrorOwnVideo}
              onTogglePin={() => {
                props.onTogglePin(dominantSlot.key);
              }}
            />
          </div>
        </div>
        {rest.length > 0 ? (
          <div
            data-testid="grid-rest"
            aria-label={t('room.rest_aria')}
            className={cn(
              'flex min-h-0 min-w-0 items-center justify-center overflow-hidden',
              paginated && 'flex-col',
            )}
            style={rowMode ? { width: sideW } : { height: Math.max(0, sideH), width: '100%' }}
          >
            <div
              className={cn('grid', paginated && 'min-h-0')}
              style={{
                gridTemplateColumns: `repeat(${sideGrid.cols}, ${sideGrid.tileWidth}px)`,
                gap: TILE_GAP_PX,
              }}
            >
              {rest.map((slot) => (
                <div
                  key={slot.key}
                  className="relative cursor-pointer overflow-hidden rounded-xl bg-zinc-900 ring-1 ring-white/[0.06]"
                  style={{ width: sideGrid.tileWidth, height: sideGrid.tileHeight }}
                  onClick={(event) => {
                    // The pin buttons inside the tiles keep their own accessible path; tile
                    // clicks that start on a button belong to the button only.
                    if (
                      event.target instanceof Element &&
                      event.target.closest('button') !== null
                    ) {
                      return;
                    }
                    props.onTogglePin(slot.key);
                  }}
                >
                  <SlotSurface
                    slot={slot}
                    speaking={speaking.has(slot.identity)}
                    pinned={props.pinnedKey === slot.key}
                    isAdmin={props.isAdminIdentity(slot.identity)}
                    handRaised={props.handRaisedFor(slot.identity)}
                    mirror={props.mirrorOwnVideo}
                    onTogglePin={() => {
                      props.onTogglePin(slot.key);
                    }}
                  />
                </div>
              ))}
            </div>
            {paginated ? (
              // The large-room pager (gridLimits.ts): only the windowed page's tiles render,
              // so the decode budget stays bounded at 40 participants.
              <div
                data-testid="grid-pager"
                className="relative z-30 flex shrink-0 items-center justify-center gap-2 pb-2"
              >
                <button
                  type="button"
                  data-testid="grid-page-prev"
                  aria-label={t('room.grid_page_prev')}
                  disabled={restPage.page === 0}
                  onClick={() => {
                    setRequestedPage(restPage.page - 1);
                  }}
                  className="grid size-8 place-items-center rounded-lg border border-white/10 bg-zinc-900/80 text-zinc-300 transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft aria-hidden className="size-4" />
                </button>
                <span
                  data-testid="grid-page-status"
                  aria-live="polite"
                  className="rounded-lg border border-white/10 bg-zinc-900/80 px-2.5 py-1 text-[11px] font-medium tabular-nums text-zinc-400"
                >
                  {t('room.grid_page_status', {
                    page: restPage.page + 1,
                    count: restPage.pageCount,
                  })}
                </span>
                <button
                  type="button"
                  data-testid="grid-page-next"
                  aria-label={t('room.grid_page_next')}
                  disabled={restPage.page >= restPage.pageCount - 1}
                  onClick={() => {
                    setRequestedPage(restPage.page + 1);
                  }}
                  className="grid size-8 place-items-center rounded-lg border border-white/10 bg-zinc-900/80 text-zinc-300 transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight aria-hidden className="size-4" />
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {pipTile !== null ? (
        <SelfPip
          tile={pipTile}
          area={{ width: box.width, height: box.height }}
          mirror={props.mirrorOwnVideo}
          speaking={speaking.has(pipTile.identity)}
          isAdmin={props.isAdminIdentity(pipTile.identity)}
          handRaised={props.handRaisedFor(pipTile.identity)}
        />
      ) : null}
    </section>
  );
}

/** The rendered surface of one slot: a camera tile or a screen-share tile, fill mode. */
function SlotSurface(props: {
  readonly slot: TileSlot;
  readonly speaking: boolean;
  readonly pinned: boolean;
  readonly isAdmin: boolean;
  readonly handRaised: boolean;
  readonly mirror: boolean;
  readonly onTogglePin: () => void;
}): JSX.Element {
  const { slot } = props;
  if (slot.kind === 'share' && slot.share !== null) {
    return (
      <ScreenTile share={slot.share} fill pinned={props.pinned} onTogglePin={props.onTogglePin} />
    );
  }
  if (slot.kind === 'camera' && slot.camera !== null) {
    return (
      <VideoTile
        tile={slot.camera}
        fill
        speaking={props.speaking}
        pinned={props.pinned}
        isAdmin={props.isAdmin}
        handRaised={props.handRaised}
        mirror={props.mirror && slot.camera.isLocal}
        onTogglePin={props.onTogglePin}
      />
    );
  }
  // Unreachable by construction (every slot carries exactly one surface).
  return <div className="h-full w-full bg-zinc-900" />;
}

/**
 * Resolves the dominant tile key on every render (and after every hysteresis beat): the pure
 * {@link resolveDominant} chain fed from a ref of the freshest input, plus a timer that fires
 * when a speaking candidate's 1.5 s ramp completes. The resolved state is reference-stable,
 * so idle renders bail out instead of looping.
 */
function useDominantTile(input: DominanceInput): string | null {
  const [state, setState] = useState<DominanceState>(INITIAL_DOMINANCE);
  const inputRef = useRef<DominanceInput>(input);
  inputRef.current = input;

  useLayoutEffect(() => {
    setState((current) => resolveDominant(current, { ...inputRef.current, nowMs: Date.now() }));
  });

  const { candidateSince, candidateKey } = state;
  useEffect(() => {
    if (candidateSince === null) {
      return;
    }
    const remaining = SPEAKER_HYSTERESIS_MS - (Date.now() - candidateSince);
    const resolve = (): void => {
      setState((current) => resolveDominant(current, { ...inputRef.current, nowMs: Date.now() }));
    };
    if (remaining <= 0) {
      resolve();
      return;
    }
    const timer = window.setTimeout(resolve, remaining + 10);
    return () => {
      window.clearTimeout(timer);
    };
  }, [candidateSince, candidateKey]);

  return state.dominant;
}
